import React from "react";
import ReactDOM from "react-dom/client";
import Chatbot from "./chatbot/Chatbot";
import Admin from "./admin/Admin";
import Chatbox from "./admin/Chatbox";
import ShopifyProvider from "./ShopifyProvider";
import Subscriptions from "./admin/Subscriptions";
import AgentDashboard from './admin/AgentDashboard';
import Summary from './admin/Summary';
import Settings from './admin/Settings';
import Help from './admin/Help';
import UpcomingFeatures from './admin/UpcomingFeatures';
import ShopifyNavigation from './admin/ShopifyNavigation';
import { SHOPIFY_OAUTH_SCOPES, config } from './constants';
import './index.css';

// === FONT OVERRIDE LOGIC MOVED FROM index.js ===
// Dynamically inject a super-specific style tag for Inter font
(function() {
  const style = document.createElement('style');
  style.innerHTML = `
    #app, #app *, 
    #app *:before, #app *:after,
    .Polaris-Frame, .Polaris-Frame *, 
    .Polaris-Frame *:before, .Polaris-Frame *:after {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
    }
  `;
  document.head.appendChild(style);
})();

const appEl = document.getElementById('app');
if (appEl) {
  appEl.style.fontFamily = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
}
// === END FONT OVERRIDE LOGIC ===

console.log('[main.jsx] window.location.pathname:', window.location.pathname);
// Always run app logic for any embedded app route
// Optionally, restrict to /apps/ path if desired:
// if (/\/apps\//.test(window.location.pathname)) {
const params = new URLSearchParams(window.location.search);
const shop = params.get('shop');
const host = params.get('host');
const isWidget = params.get('widget') === 'true';
const isEmbeddedShopify = shop && host;

console.log('[main.jsx] params:', Object.fromEntries(params.entries()));
console.log('[main.jsx] shop:', shop, 'host:', host, 'isWidget:', isWidget, 'isEmbeddedShopify:', isEmbeddedShopify);

// Debug log for what you CAN access in the iframe
console.log('[main.jsx] window.location.pathname:', window.location.pathname);
console.log('[main.jsx] window.location.href:', window.location.href);
console.log('[main.jsx] document.referrer:', document.referrer);
console.log('[main.jsx] host param:', new URLSearchParams(window.location.search).get('host'));

// --- Force embedded context if not in iframe and host param is present ---
// Prevent infinite redirect: Only redirect if NOT already in Shopify admin domain
const isInShopifyAdmin = (
  (document.referrer && document.referrer.includes('admin.shopify.com')) ||
  window.location.hostname.includes('admin.shopify.com') ||
  window.top !== window.self
);

// Extract store handle from shop domain for Shopify embedded app URL
function getStoreHandle(shop) {
  return shop ? shop.replace('.myshopify.com', '') : '';
}

const storeHandle = getStoreHandle(shop);
if (!isInShopifyAdmin && host && shop) {
  console.log('[main.jsx] Redirecting to embedded Shopify admin URL...');
  window.location.assign(`https://admin.shopify.com/store/${storeHandle}/apps/${config.APP_HANDLE}?${window.location.search.substring(1)}`);
}

console.log("App loaded! isWidget:", isWidget, "isEmbeddedShopify:", isEmbeddedShopify, "params:", window.location.search);

// Extract route from pathname for embedded Shopify app
function getAppRoute() {
  const params = new URLSearchParams(window.location.search);
  const view = params.get('view');
  if (view === 'dashboard') return '/dashboard';
  if (view === 'admin') return '/admin';
  if (view === 'livechat') return '/livechat';
  if (view === 'subscriptions') return '/subscriptions';
  if (view === 'settings') return '/settings';
  if (view === 'help') return '/help';
  // Default: use pathname (for legacy support)
  return window.location.pathname.replace(/\/store\/[^/]+\/apps\/[^/]+/, '') || '/';
}

function AppFrame({ shop }) {
  // Use getAppRoute() for initial route
  const [route, setRoute] = React.useState(getAppRoute());

  // Update route on popstate
  React.useEffect(() => {
    const onPopState = () => setRoute(getAppRoute());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  
  function navigate(path) {
    // Build the full path for Shopify admin
    const prefixMatch = window.location.pathname.match(/(\/store\/[^/]+\/apps\/[^/]+)/);
    let newPath = path;
    if (prefixMatch) newPath = prefixMatch[1] + path;

    // Preserve query params (host, shop, etc)
    const search = window.location.search;
    window.history.pushState({}, '', newPath + search);
    setRoute(path);
  }

  // Debug logs for routing
  console.log('window.location.pathname:', window.location.pathname);
  console.log('route:', route);

  return (
    <ShopifyProvider>
      <div style={{ display: 'flex', height: '100vh', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <div style={{ width: 0, background: '#f4f6f8', minHeight: '100vh' }}>
          <ShopifyNavigation navigate={navigate} shop={shop} />
        </div>
        <div style={{ flex: 1, padding: 24, height: '100vh', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          {(route === '/' || route === '/dashboard' || route === '/frontend-entry') ? (
            <Summary shop={shop} navigate={navigate} />
          ) : route === '/admin' ? (
            <Admin storeId={shop} />
          ) : route === '/livechat' ? (
            <AgentDashboard storeId={shop} />
          ) : route === '/subscriptions' ? (
            <Subscriptions shop={shop} />
          ) : route === '/settings' ? (
            <Settings />
          ) : route === '/help' ? (
            <Help />
          ) : route === '/upcoming-features' ? (
            <UpcomingFeatures />
          ) : route === '/chatbox' ? (
            <Chatbox />
          ) : (
            <div />
          )}
        </div>
      </div>
    </ShopifyProvider>
  );
}

function renderApp() {
  console.log('[renderApp] isWidget:', isWidget, '| isEmbeddedShopify:', isEmbeddedShopify, '| shop:', shop, '| host:', host, '| params:', Object.fromEntries(params.entries()));
  console.log('[renderApp] window.location:', window.location.href);
  console.log('[renderApp] document.referrer:', document.referrer);
  if (isWidget) {
    console.log('[renderApp] Branch: Widget mode');
    let widgetDiv = document.getElementById('shopify-chatbot-widget');
    if (!widgetDiv) {
      widgetDiv = document.createElement('div');
      widgetDiv.id = 'shopify-chatbot-widget';
      document.body.appendChild(widgetDiv);
    }
    ReactDOM.createRoot(widgetDiv).render(
      <React.StrictMode>
        <ShopifyProvider>
          <Chatbot />
        </ShopifyProvider>
      </React.StrictMode>
    );
  } else if (isEmbeddedShopify) {
    console.log('[renderApp] Branch: Embedded Shopify admin UI');
    console.log('[renderApp] Rendering AppFrame with shop:', params.get('shop'));
    ReactDOM.createRoot(document.getElementById("root")).render(
      <React.StrictMode>
        <ShopifyProvider>
          <AppFrame shop={params.get('shop')} />
        </ShopifyProvider>
      </React.StrictMode>
    );
  } else {
    console.log('[renderApp] Branch: Standalone chatbot UI');
    ReactDOM.createRoot(document.getElementById("root")).render(
      <React.StrictMode>
        <ShopifyProvider>
          <Chatbot />
        </ShopifyProvider>
      </React.StrictMode>
    );
  }
}

renderApp();